
import { useState, useEffect, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import './App.css'
import supabase, { fetchSalesLightweight, fetchMostRecentSaleDate, getSalesVolumeData } from './supabase-client'
import { createDate, getDaysAgo, dateToISOString } from './utils/dateUtils.js'
import { DataCacheProvider } from './contexts/DataCacheContext'
import { Toaster } from 'sonner'
import LandingPage from './components/LandingPage'
import Login from './components/Login'
import Register from './components/Register'
import ResetPassword from './components/ResetPassword'
import AppContent from './components/AppContent'
import SplashScreenDemo from './components/SplashScreenDemo'
import ErrorBoundary from './components/ErrorBoundary'
// import PerformanceDebugger from './components/PerformanceDebugger'
import XeroAuthCallback from './components/xero/XeroAuthCallback'
import FeedbackWidget from './components/feedback/FeedbackWidget'
import DemoIndex from './pages/demo/DemoIndex'
import ModalDemo from './pages/demo/ModalDemo'
import TimelineDividerDemo from './components/sales-timeline/TimelineDividerDemo'

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showRegister, setShowRegister] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [hasAccess, setHasAccess] = useState(true) // Temporarily disabled landing page
  
  // Shared reports data for dashboard components
  const [sharedReportsData, setSharedReportsData] = useState([])
  const [reportsDataLoading, setReportsDataLoading] = useState(false)

  // Preload reports data for dashboard performance
  const preloadReportsData = useCallback(async () => {
    try {
      console.log('[PRELOAD] Starting preload of reports data...')
      setReportsDataLoading(true)
      
      const reportsData = await getSalesVolumeData('all')
      setSharedReportsData(reportsData)
      
      console.log(`[PRELOAD] Preloaded ${reportsData.length} months of reports data`)
    } catch (error) {
      console.error('[PRELOAD] Failed to preload reports data:', error)
      setSharedReportsData([])
    } finally {
      setReportsDataLoading(false)
    }
  }, [])

  // Preload sales data for better performance
  const [preloadAttempted, setPreloadAttempted] = useState(false)

  const preloadRecentSales = useCallback(async () => {
    // Prevent infinite loop: only attempt preload once
    if (preloadAttempted) {
      console.log('[PRELOAD] Skipping - already attempted')
      return
    }

    setPreloadAttempted(true)

    try {
      console.log('[PRELOAD] Finding most recent sale...')

      // CRITICAL FIX: Use lightweight query that only fetches the date field
      // This prevents loading all 368 sales with nested sale_items causing ERR_INSUFFICIENT_RESOURCES
      const mostRecentSaleDate = await fetchMostRecentSaleDate()

      let startDate, endDate

      if (mostRecentSaleDate) {
        // Load 90 days backwards from the most recent sale
        const mostRecentDate = createDate(mostRecentSaleDate)
        const past90FromRecent = createDate(mostRecentDate.getTime() - (90 * 24 * 60 * 60 * 1000))
        startDate = dateToISOString(past90FromRecent)
        endDate = dateToISOString(mostRecentSaleDate)
        console.log(`[PRELOAD] Loading 90 days from most recent sale (${endDate})...`)
      } else {
        // Fallback to past 90 days from today if no sales exist
        const now = createDate()
        const past90Days = getDaysAgo(90)
        startDate = dateToISOString(past90Days)
        endDate = dateToISOString(now)
        console.log('[PRELOAD] No sales found, loading past 90 days from today...')
      }

      const recentSales = await fetchSalesLightweight(startDate, endDate)

      // Cache the preloaded data
      sessionStorage.setItem('preloaded_recent_sales', JSON.stringify({
        data: recentSales,
        dateRange: { startDate, endDate },
        timestamp: Date.now()
      }))

      console.log(`[PRELOAD] Preloaded ${recentSales.length} recent sales records`)

      // Reports data is now loaded separately only when needed (not automatically)
    } catch (error) {
      console.error('[PRELOAD] Failed to preload recent sales:', error)
      // Don't retry on error - prevents infinite loop
    }
  }, [preloadAttempted])

  useEffect(() => {
    // Check if we're on the reset password page
    if (window.location.pathname === '/reset-password' || window.location.hash.includes('reset-password')) {
      setShowResetPassword(true)
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      let user = session?.user ?? null

      // Dev mode auto-login
      const shouldAutoLogin = import.meta.env.VITE_DEV_AUTO_LOGIN === 'true'
      const devEmail = import.meta.env.VITE_DEV_EMAIL
      const devPassword = import.meta.env.VITE_DEV_PASSWORD

      if (!user && shouldAutoLogin && devEmail && devPassword) {
        console.log('[DEV] Auto-login enabled, signing in with dev credentials...')
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email: devEmail,
            password: devPassword,
          })

          if (error) {
            console.error('[DEV] Auto-login failed:', error.message)
          } else {
            user = data.user
            console.log('[DEV] Auto-login successful')
          }
        } catch (error) {
          console.error('[DEV] Auto-login error:', error)
        }
      }

      setUser(user)
      setLoading(false)

      // Start preloading data after successful authentication
      // Only preload data actually needed for the current page to reduce bandwidth
      if (user) {
        const currentPath = window.location.pathname

        // Only preload sales data if on sales or dashboard page
        if (['/sales', '/dashboard', '/'].includes(currentPath)) {
          setTimeout(() => preloadRecentSales(), 1000) // Slight delay to let UI settle
        }

        // Only preload reports data if on reports page
        if (currentPath === '/reporting') {
          setTimeout(() => preloadReportsData(), 1000)
        }
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const user = session?.user ?? null
      setUser(user)
      setLoading(false)

      // Handle password recovery
      if (event === 'PASSWORD_RECOVERY') {
        setShowResetPassword(true)
        setShowRegister(false)
      }

      // Start preloading on successful login/registration
      // Only preload data actually needed for the current page to reduce bandwidth
      if (user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        const currentPath = window.location.pathname

        // Only preload sales data if on sales or dashboard page
        if (['/sales', '/dashboard', '/'].includes(currentPath)) {
          setTimeout(() => preloadRecentSales(), 1000)
        }

        // Only preload reports data if on reports page
        if (currentPath === '/reporting') {
          setTimeout(() => preloadReportsData(), 1000)
        }
      }
      
      // Clear cached data on logout
      if (!user && event === 'SIGNED_OUT') {
        sessionStorage.removeItem('preloaded_recent_sales')
        setSharedReportsData([])
        setReportsDataLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [preloadRecentSales, preloadReportsData])

  const handleLogin = (user) => {
    setUser(user)
  }

  const handleRegister = (user) => {
    setUser(user)
  }

  const handleLogout = () => {
    setUser(null)
  }

  const handleSwitchToRegister = () => {
    setShowRegister(true)
  }

  const handleSwitchToLogin = () => {
    setShowRegister(false)
    setShowResetPassword(false)
    // Clear the URL hash/path if needed
    if (window.location.pathname === '/reset-password' || window.location.hash.includes('reset-password')) {
      window.history.replaceState({}, document.title, '/')
    }
  }

  const handleResetComplete = () => {
    setShowResetPassword(false)
    setShowRegister(false)
    // Clear the URL and redirect to login
    window.history.replaceState({}, document.title, '/')
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading Odin...</p>
        </div>
      </div>
    )
  }

  return (
    <Router>
      <div className="App">
        {/* Development-only performance monitor */}
        {/* {process.env.NODE_ENV === 'development' && <PerformanceDebugger />} */}

        {/* Toast notifications */}
        <Toaster position="top-right" expand={true} richColors />

        <Routes>
          {/* Demo pages - public routes for testing/development */}
          <Route path="/demo" element={<DemoIndex />} />
          <Route path="/demo/timeline-dividers" element={<TimelineDividerDemo />} />
          <Route path="/splash-demo" element={<SplashScreenDemo />} />
          <Route path="/modal-demo" element={<ModalDemo />} />

          {/* Xero OAuth callback route - must be before catch-all */}
          <Route
            path="/settings/integrations/xero/callback"
            element={user ? <XeroAuthCallback user={user} /> : <Login onLogin={handleLogin} onSwitchToRegister={handleSwitchToRegister} />}
          />

          {/* Main app routes */}
          <Route
            path="*"
            element={
              !hasAccess ? (
                <LandingPage onAccessGranted={() => setHasAccess(true)} />
              ) : user && !showResetPassword ? (
                <DataCacheProvider>
                  <AppContent
                    user={user}
                    onLogout={handleLogout}
                    sharedReportsData={sharedReportsData}
                    reportsDataLoading={reportsDataLoading}
                    preloadReportsData={preloadReportsData}
                  />
                  <FeedbackWidget user={user} />
                </DataCacheProvider>
              ) : showResetPassword ? (
                <ResetPassword onResetComplete={handleResetComplete} onBackToLogin={handleSwitchToLogin} />
              ) : showRegister ? (
                <Register onRegister={handleRegister} onSwitchToLogin={handleSwitchToLogin} />
              ) : (
                <Login onLogin={handleLogin} onSwitchToRegister={handleSwitchToRegister} />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  )
}

export default App
